import { useState, type ReactNode } from 'react';
import { ErrorNote, Modal } from './ui';

/**
 * Two-step confirmation used for every action that changes something important.
 *  Step 1 — "Review": the form or an explanation of what will happen. Continue may fetch a preview from the API.
 *  Step 2 — "Confirm": a plain summary of exactly what will change, and a second, clearly worded button.
 * Nothing is changed until that second button is pressed.
 */
export function ConfirmFlow<R = unknown>({
  title, onClose, children, canContinue = true, continueLabel = 'Continue', review, confirmLabel, danger, onConfirm, onDone,
}: {
  title: string;
  onClose: () => void;
  /** Step 1 content (form fields, or what will happen). */
  children: ReactNode;
  canContinue?: boolean;
  continueLabel?: string;
  /** Builds the step 2 summary; may call a preview endpoint. Throwing keeps the user on step 1 with the error shown. */
  review: () => ReactNode | Promise<ReactNode>;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => Promise<R>;
  onDone?: (r: R) => void;
}) {
  const [step, setStep] = useState<1 | 2>(1);
  const [summary, setSummary] = useState<ReactNode>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const next = async () => {
    setBusy(true); setError(null);
    try { setSummary(await review()); setStep(2); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const go = async () => {
    setBusy(true); setError(null);
    try { const r = await onConfirm(); onDone?.(r); onClose(); } catch (e) { setError(e); setBusy(false); }
  };

  return (
    <Modal title={step === 1 ? title : `Confirm: ${title.charAt(0).toLowerCase()}${title.slice(1)}`} onClose={busy ? () => {} : onClose} wide>
      <ol className="confirm-steps" aria-label="Steps">
        <li className={step === 1 ? 'on' : 'done'}>1 · Review</li>
        <li className={step === 2 ? 'on' : ''}>2 · Confirm</li>
      </ol>
      {step === 1 ? (
        <form className="stack" onSubmit={(e) => { e.preventDefault(); if (canContinue && !busy) void next(); }}>
          {children}
          {error != null && <ErrorNote error={error} />}
          <div className="row gap">
            <button className="btn primary" disabled={!canContinue || busy}>{busy ? 'Checking…' : continueLabel}</button>
            <button type="button" className="btn" onClick={onClose}>Cancel</button>
          </div>
        </form>
      ) : (
        <div className="stack">
          <div className={`callout ${danger ? 'bad' : 'warn'} confirm-summary`}>{summary}</div>
          <p className="small dim">Please check the details above. Nothing has changed yet — this happens only when you press the button below.</p>
          {error != null && <ErrorNote error={error} />}
          <div className="row gap">
            <button className={`btn ${danger ? 'danger-fill' : 'primary'}`} disabled={busy} onClick={() => void go()}>{busy ? 'Working…' : confirmLabel}</button>
            <button className="btn" disabled={busy} onClick={() => { setStep(1); setError(null); }}>Back</button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** "12 Oct 2026 → 19 Oct 2026" style line used in summaries. */
export function Change({ label, from, to }: { label: string; from: ReactNode; to: ReactNode }) {
  const same = from === to;
  return <li><strong>{label}:</strong> {same ? <>{to} <span className="dim">(no change)</span></> : <><s className="dim">{from || '—'}</s> → <strong>{to || '—'}</strong></>}</li>;
}
